import { createContext, useContext, useEffect, useState } from "react";
import { api, setAuthToken } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [office, setOffice] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [legalPending, setLegalPending] = useState(0);
  const [consents, setConsents] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setOffice(data.office);
      setSubscription(data.subscription);
      setLegalPending(data.legal_pending || 0);
      setConsents(data.consents || {});
    } catch {
      setUser(null);
      setOffice(null);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        const code = err.response?.data?.detail?.code;
        if (err.response?.status === 423 || err.response?.status === 428 || code === "FEATURE_LOCKED") refresh();
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  const login = async (payload) => {
    const { data } = await api.post("/auth/login", payload);
    setAuthToken(data.token);
    await refresh();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setAuthToken(null);
    setUser(null);
    setOffice(null);
    setSubscription(null);
  };

  const hasFeature = (key) => {
    if (!key || !user?.office_id) return true;
    return !!subscription?.features?.includes(key);
  };

  const hasPerm = (perm) => user?.role === "SUPER_ADMIN" || !!user?.permissions?.includes(perm);

  return (
    <AuthContext.Provider value={{ user, office, subscription, legalPending, consents, loading, login, logout, refresh, hasFeature, hasPerm }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
